// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Every sentence this tool says about its own limits.
 *
 * These are in one file because they are the product. A quotation checker that
 * overstates what it knows is worse than no quotation checker: it gets someone
 * to delete a correct quotation, or to accuse a colleague of inventing one. The
 * honesty gate below is not decoration around the feature — it IS the feature,
 * and it is kept here so that changing it is a deliberate act rather than a
 * side effect of editing a template.
 *
 * Three words are banned from every verdict string in this file and everywhere
 * else: "fabricated", "hallucination", and "plagiarism". This tool observes
 * whether words appear in files. It does not know why they differ.
 */

import type { ExtractedSource, IdentifierVerdict, MatchClass } from './types.ts';

export const TAGLINE =
  'Check every quotation in your draft against the source files on your own disk.';

export const CLASS_LABELS: Record<MatchClass, string> = {
  exact: 'Exact',
  'exact-ellipsis': 'Exact, with an ellipsis',
  near: 'Near match',
  altered: 'Substantially altered',
  elsewhere: 'Found in a different source',
  'not-found': 'Not found',
  unreadable: 'Not in the files I could read',
  'not-checked': 'Not checked',
};

export const CLASS_BLURBS: Record<MatchClass, string> = {
  exact: 'Every word matches the source as I read it.',
  'exact-ellipsis': 'Every segment matches; the elisions you marked really were elisions.',
  near: 'A small number of words differ. The differences are shown.',
  altered: 'Enough words differ that this reads as a paraphrase inside quotation marks.',
  elsewhere: 'These words are in one of your files — but not the one the draft points at.',
  'not-found': 'These words are not in the files you supplied, as I read them.',
  unreadable:
    'These words are not in the files that have a text layer. One of your files does not, so I cannot tell you whether the passage is in there — and I will not guess.',
  'not-checked': 'I did not adjudicate this one, and it is in no count of problems.',
};

/** Shown once per results view, and inside both exported artefacts. */
export const EXACTNESS_CAVEAT =
  '"Exact" means exact against the text I extracted from the file, not against the file\'s bytes. Extraction repairs words broken across lines, joins lines with a space, and decomposes ligatures, so a match can be genuine even where the characters differ.';

/** Shown on every NOT FOUND row, and inside both exported artefacts. */
export const NOT_FOUND_CAVEAT =
  'Not found in the files you supplied. That means the words are not in these files as I read them — it does not mean the quotation is wrong, and it does not mean the source is wrong. The passage may be in a work you did not give me, or on a page I could not read.';

export const PREFLIGHT_HEADING = 'I can only check what I can read. Here is what I got out of each file.';

export const UNSEARCHABLE_NOTE =
  'This file has no text layer — it is pictures of pages, not text. I could not check any quotation against it, and I have not marked anything as missing because of it.';

export const IMAGE_PAGE_NOTE = (sheet: number): string =>
  `Page ${sheet} has no text layer. If your quotation is on that page, I would not find it.`;

export const IMAGE_PAGES_NOTE = (sheets: number[]): string =>
  sheets.length === 1
    ? IMAGE_PAGE_NOTE(sheets[0])
    : `Pages ${sheets.slice(0, 6).join(', ')}${
        sheets.length > 6 ? '…' : ''
      } have no text layer. If your quotation is on one of them, I would not find it.`;

/**
 * Everything the preflight can say about one file, in plain text.
 *
 * This exists because the screen and the exported report used to assemble it
 * separately, and the report's version quietly said less: it dropped the
 * per-page "no text layer" warning, so a 200-sheet PDF with eight scanned
 * sheets read, in the artefact that gets forwarded, as though every page had
 * been searched — and it described a file that would not open at all as a scan
 * needing OCR, which sends the reader to the wrong remedy. One builder, so the
 * two cannot drift again. The caller escapes these strings; they are prose,
 * not markup, and `source.error` is a message from a parser about somebody
 * else's file.
 */
export function sourceNoteLines(source: ExtractedSource): string[] {
  // Order matters, and it is the same order the screen uses: the real reason a
  // file could not be read outranks the generic one.
  if (source.error) return [source.error];
  if (source.unsearchable) return [UNSEARCHABLE_NOTE];

  const lines: string[] = [];
  if (source.pageNote) lines.push(source.pageNote);
  const imagePages = source.pages.filter((p) => p.imageOnly).map((p) => p.sheet);
  if (imagePages.length > 0) lines.push(IMAGE_PAGES_NOTE(imagePages));
  if (source.runningLinesDropped > 0) {
    lines.push(
      `${source.runningLinesDropped} repeating running-head or footer line${
        source.runningLinesDropped === 1 ? '' : 's'
      } removed before matching.`,
    );
  }
  return lines;
}

export const SHEET_ONLY_NOTE =
  'This file gives no printed page numbers, so I can only tell you the sheet number. I cannot tell you whether your pinpoint is right.';

export const NO_PAGINATION_NOTE =
  'This format does not store page numbers, so I have not checked your pinpoint against it.';

export const ORTHOGRAPHIC_NOTE =
  'The only differences here are spelling. This is a house-style difference, not a misquotation.';

export const IDENTIFIER_NOTE =
  'I checked that this identifier resolves to a work with this title, author and year. I did not check whether the work says what you say it says.';

/**
 * Printed above the identifier table, on screen and in the report.
 *
 * It used to open "I checked that each identifier resolves…", which is a claim
 * that the check ran. When a registry cannot be reached the check did not run,
 * and the artefact was asserting otherwise while showing a red verdict.
 */
export const IDENTIFIER_TABLE_CAVEAT =
  'Where I could reach a registry, I checked that the identifier resolves to a work with this title, author and year. I did not check whether the work says what you say it says.';

/**
 * What each identifier verdict is called, on screen and in the report.
 *
 * The raw enum member used to be printed. Two of them read as accusations when
 * they are not: "unresolvable" is a statement about the registries' answer, and
 * "not-checked" means no registry answered at all. Naming them in sentences
 * keeps the difference visible in an artefact read a long way from here.
 */
export const IDENTIFIER_VERDICT_LABELS: Record<IdentifierVerdict, string> = {
  match: 'Resolves',
  'title-mismatch': 'Title differs',
  'year-mismatch': 'Year differs',
  'author-mismatch': 'Author differs',
  unresolvable: 'No registry has it',
  'rate-limited': 'Registry asked me to slow down',
  'not-checked': 'Not checked',
};

/** The verdicts that are not a finding about the identifier, only about my reach. */
export const IDENTIFIER_INCONCLUSIVE: IdentifierVerdict[] = ['not-checked', 'rate-limited'];

export const IDENTIFIER_UNREACHABLE_NOTE =
  'I could not reach the registry, so I have not checked this one. That is a fact about the network between me and them, not about your identifier.';

export const IDENTIFIER_RATE_LIMITED_NOTE =
  'The registry asked me to slow down before it answered, so I have not checked this one.';

/** Printed above the identifier table when some of them were never checked. */
export const IDENTIFIER_UNREACHABLE_CAVEAT = (count: number): string =>
  count === 1
    ? 'One identifier below was never checked: I could not reach a registry for it. Read that row as a gap in this run, not as a finding.'
    : `${count} identifiers below were never checked: I could not reach a registry for them. Read those rows as gaps in this run, not as findings.`;

export const HEADLINE_NOTE =
  'I do not produce a score, and I cannot tell you whether a quotation is honest — only whether these words are in these files.';

export const TRUST_BANNER =
  'Your draft and your sources are read in this tab and never uploaded.';

export const HOW_IT_WORKS = `
<ol class="steps">
  <li>
    <h3>You give it a draft and the sources you actually hold</h3>
    <p>A draft as Markdown, plain text or Word. Sources as PDF, Word, EPUB, text or HTML. Nothing is
    uploaded — the files are read with the browser's own file reader and parsed here, in this tab.</p>
  </li>
  <li>
    <h3>Each source is turned back into readable prose</h3>
    <p>A PDF does not contain paragraphs. It contains thousands of positioned fragments, and putting
    them back in order is most of the work. Fragments are grouped into lines by where they sit on the
    page, two-column layouts are split at the gutter, spaces are inferred from the actual horizontal
    gaps rather than guessed, and repeating running heads are removed so that a quotation running
    across a page break does not have <span class="mono">48 Chapter 3</span> spliced into its middle.</p>
  </li>
  <li>
    <h3>Before anything else, it tells you what it could read</h3>
    <p>Every file gets a character count. A file with no text layer — a scan, a photograph of a page —
    is marked <strong>unsearchable</strong>, and it is then structurally incapable of producing a
    <em>not found</em>. That is the difference between a result and an accusation.</p>
  </li>
  <li>
    <h3>Quotations are lifted out of the draft by their delimiters</h3>
    <p>Curly and straight quotation marks, guillemets, CJK brackets, Markdown blockquotes and indented
    block quotations. Never an apostrophe on its own: in one chapter of Darwin there are 147 right
    single quotes and only nine of them open a quotation, so treating them as delimiters would invent
    a dozen quotations per chapter out of ordinary possessives.</p>
  </li>
  <li>
    <h3>Matching finds where each one is, and how it differs</h3>
    <p>An index of five-word sequences over every source narrows a quotation down to a handful of
    places worth looking at; each is then confirmed by aligning the quotation against the source word
    by word. The alignment is <em>semi-global</em> — the page may start and end anywhere, but the
    whole quotation must line up. That is what stops a twenty-word quotation with a changed fourth
    word from being reported as a perfect match on its first three words.</p>
    <p>Hyphens and apostrophes are treated as joiners, so a word the typesetter broke across a line
    still matches the word you quoted. This is the single most common reason searching a PDF by hand
    fails.</p>
  </li>
  <li>
    <h3>Printed page numbers are read off the page</h3>
    <p>To check a pinpoint — you cited page 14, is it on page 14? — the tool needs the number printed
    on the page, not the sheet index. It finds the folio by looking for a number that sits in the same
    place on every page and counts up by one, and cross-checks it against the file's own page labels.
    Where the two disagree, or where neither can be read, it says so and never calls a pinpoint wrong.</p>
  </li>
  <li>
    <h3>You leave with two files</h3>
    <p>A CSV with one row per quotation, and a self-contained HTML report with your draft annotated
    and every difference shown. Both carry their own caveats inside them, because a report that is
    forwarded gets read a long way from the screen that explained it.</p>
  </li>
</ol>`;

export const PRIVACY = `
<p class="lede">Your unpublished draft and your source files are exactly the two things you cannot
hand to a vendor. They never leave this tab.</p>

<h3>Protected</h3>
<ul>
  <li><strong>Every file you drop in.</strong> They are read with the browser's file reader and parsed
  in this page. There is no upload, in any mode, at any point. There is no server behind this page to
  upload them to.</li>
  <li><strong>Every quotation, and every result.</strong> The matching, the alignment and the page
  numbering all happen on your machine.</li>
  <li><strong>Both artefacts.</strong> The CSV and the HTML report are assembled here and handed to
  you as a download.</li>
  <li><strong>None of your text is written to disk.</strong> No IndexedDB, no Origin Private File
  System, no saved copy of your draft, your sources or your results — not one byte of them is written
  anywhere. A build check refuses to publish the site if those APIs appear in the code I wrote, so
  this stays true rather than merely being intended.</li>
  <li><strong>The one thing written to disk is this app itself.</strong> About 2.5 MB of the page's
  own code and icons are stored by its service worker, which is what lets it open with the network
  off. That cache holds the tool, never your files.</li>
  <li><strong>It works offline.</strong> Install it, pull the network cable, and it still works. That
  is the strongest form of the promise: a page that is not talking to anything cannot be sending
  anything.</li>
</ul>

<h3>Not protected</h3>
<ul>
  <li><strong>If you switch on “Check identifiers online”</strong> — off by default — the DOIs, PMIDs
  and arXiv ids <em>from your draft</em> are sent to four public registries. Those registries learn
  which papers you are citing, and see your browser's address. Nothing else is sent: not the
  quotations, not the draft, not one byte of a source file.</li>
  <li><strong>Loading this page.</strong> GitHub Pages serves the files and sees your IP address, as
  any website does.</li>
  <li><strong>The limits of extraction.</strong> A quotation that is genuinely in a source can be
  misreported if the text came out of the file mangled. Every guard I know of is in place and tested,
  but this is the honest residual risk, and it is why every <em>not found</em> row shows you what was
  actually read.</li>
  <li><strong>What a match means.</strong> This tool observes whether words appear in files. It cannot
  tell you whether a quotation is fair, whether it is in context, or whether the source is any good.</li>
</ul>

<h3>Trust surface</h3>
<ul>
  <li>The static bundle served from GitHub Pages, and the TLS connection to it.</li>
  <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting,
  no cross-site tracking; your files/data are never sent to it.</li>
  <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you
  open the feedback form and press Send; your files and data never are.</li>
  <li><span class="mono">api.crossref.org</span>, <span class="mono">api.datacite.org</span>,
  <span class="mono">api.openalex.org</span> and <span class="mono">eutils.ncbi.nlm.nih.gov</span> —
  and only when you turn the identifier check on. Those four are the whole registry list; no fifth
  one is in the page's content policy, so it could not reach one even if the code asked.</li>
</ul>`;

export const ABOUT = `
<p>quotewitness checks the quotations in a draft against source files you already have, entirely in
your browser.</p>

<p>Other citation checkers verify that a reference <em>exists</em>. This one verifies that the
sentence you put in quotation marks is actually on the page you cited — by reading the PDFs, Word
files and EPUBs on your own disk. That is a narrower claim than it sounds, and it is the honest one:
tools that do this well already exist for lawyers, against proprietary case-law databases, at
enterprise prices. None of them can open the folder of papers on your laptop.</p>

<p>The competitor worth taking seriously is Ctrl+F, which is free and already open. It loses on the
things that are hard rather than the things that are clever: a word the typesetter broke across a
line, an <span class="mono">ﬁ</span> ligature, a quotation that runs over a page break or across two
columns, and any quotation with a single word changed. Those are what this is built to survive.</p>

<p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.
The rest of the catalogue is at
<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.
Source: <a href="https://github.com/ben-gy/quotewitness" target="_blank" rel="noopener">github.com/ben-gy/quotewitness</a>.</p>

<p class="fine">Page numbering was measured against 24 documents and 3,429 pages spanning 17 distinct
PDF producer strings and around eight layout engines. Printed page numbers were recovered for roughly
88–90% of pages by reading the folio alone, and about 96% once the file's own page labels are used as
a cross-check, with no wrong label observed on any page whose true number could be established
independently. Coverage on messier files — un-OCR'd scans, right-to-left and CJK documents — is
unmeasured and should be assumed lower.</p>`;
